import { CommonModule } from '@angular/common';
import {
  HttpClient,
  HttpBackend,
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi
} from '@angular/common/http';
import {
  Injectable,
  Injector,
  ModuleWithProviders,
  NgModule,
  inject,
  provideAppInitializer
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { SharedModule } from '@bepatient/shared';
import { IonicModule } from '@ionic/angular';
import { EffectsModule } from '@ngrx/effects';
import {
  routerReducer,
  RouterStateSerializer,
  StoreRouterConnectingModule,
  FullRouterStateSerializer
} from '@ngrx/router-store';
import { ActionReducer, MetaReducer, Store, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { localStorageSync } from 'ngrx-store-localstorage';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import { CoreAppDynamicMenuComponent } from './app/dynamic-menu/app-dynamic-menu.component';
import { DynamicMenuIconComponent } from './app/dynamic-menu/components/dynamic-menu-icon/dynamic-menu-icon.component';
import { MenuMainButtonComponent } from './app/dynamic-menu/components/menu-main-button/menu-main-button.component';
/* tslint:disable:max-line-length */
import { MenuSecondaryButtonContainerComponent } from './app/dynamic-menu/components/menu-secondary-button-container/menu-secondary-button-container.component';
/* tslint:enable:max-line-length */
import { MenuSecondaryButtonComponent } from './app/dynamic-menu/components/menu-secondary-button/menu-secondary-button.component';
import { CoreAppStaticMenuComponent } from './app/static-menu/app-static-menu.component';
import { ConfirmPasswordPopupComponent } from './components/confirm-password-popup/confirm-password-popup.component';
import { ExternalLinkPopupComponent } from './components/external-link-popup/external-link-popup.component';
import { MeasurePopupComponent } from './components/measure-popup/measure-popup.component';
import { CoreApiService } from './core-api.service';
import { CoreExternalAppService } from './core-external-app.service';
import { CoreHttpResponseInterceptor } from './core-http-response.interceptor';
import { CoreHttpClient } from './core-http.client';
import { CoreHttpEffects } from './core-http.effects';
import { CoreHttpInterceptor } from './core-http.interceptor';
import { coreHttpInitialState, coreHttpReducer } from './core-http.reducer';
import { CorePopupService } from './core-popup.service';
import { CoreRouterEffects } from './core-router.effects';
import { CoreRouterSerializer } from './core-router.serializer';
import { CoreUtilsService } from './core-utils';
import { BiometricService } from './biometric.service';
import {
  CoreActionAddPageModule,
  CoreActionAppInitialized,
  CoreActionSetEntryPageModules,
  CoreActionSetInstancesData,
  CoreActionSetRootPageModule,
  CoreActionSetUseGlobalLoading
} from './core.actions';
import { CoreEffects } from './core.effects';
import { CoreEffectsMeasure } from './core.effects.measure';
import { CoreEffectsPlatform } from './core.effects.platform';
import {
  CORE_BACKEND,
  CORE_CONFIG,
  CORE_I18N_FOLDER,
  CORE_PAGE_MODULE,
  CoreBackendIDs,
  CoreConfig,
  CorePageModule
} from './core.models';
import { coreInitialState, coreReducer } from './core.reducer';
import { environment } from 'apps/smart-app/src/environments/environment';
import { format } from 'date-fns';

export const metaReducers: MetaReducer<any>[] = [localStorageSyncReducer];

export function getCoreModuleInitialState() {
  return { core: coreInitialState, http: coreHttpInitialState, router: {} };
}

@Injectable()
export class CoreInitializer {
  private store = inject<Store<any>>(Store);
  private injector = inject(Injector);

  private forRootInitialized = false;

  forAppInitializer(): Promise<void> {
    return new Promise<void>(resolve => {
      (this.injector.get<CorePageModule[]>(CORE_PAGE_MODULE) || [])
        .filter(pageModule => !!pageModule.ID)
        .forEach(pageModule =>
          this.store.dispatch(new CoreActionAddPageModule(pageModule))
        );

      this.store.dispatch(new CoreActionAppInitialized());
      resolve();
    });
  }

  forRootInitializer(): Promise<void> {
    if (this.forRootInitialized) {
      throw new Error('CoreModule.forRoot(...) has already been called !!');
    } else {
      this.forRootInitialized = true;
    }

    return new Promise<void>(resolve => {
      const config = this.injector.get(CORE_CONFIG);

      if (config.entryPageModules) {
        this.store.dispatch(
          new CoreActionSetEntryPageModules(config.entryPageModules)
        );
      }

      if (config.instancesData) {
        this.store.dispatch(
          new CoreActionSetInstancesData({
            ...config.instancesData,
            fixed: true
          })
        );
      }

      if (config.useGlobalLoading) {
        this.store.dispatch(new CoreActionSetUseGlobalLoading(true));
      }

      this.store.dispatch(
        new CoreActionSetRootPageModule(config.rootPageModule)
      );

      resolve();
    });
  }
}

// const myFormModule: CorePageModule = {
//   ID: CorePageModuleIDs.MY_FORM,
//   leftMenuAvailableForDepthModules: true,
//   routes: CoreUtils.generateDepthModuleRoutes(
//     'my-form',
//     () =>
//       import('../../../my-form/src/lib/pages/my-form/my-form.module').then(
//         module => module.MyFormPageModule
//       ),
//       CoreInitializer
//   )
// };

@NgModule({
  declarations: [
    CoreAppStaticMenuComponent,
    CoreAppDynamicMenuComponent,
    MenuSecondaryButtonContainerComponent,
    MenuSecondaryButtonComponent,
    DynamicMenuIconComponent,
    MenuMainButtonComponent,
    MeasurePopupComponent,
    ConfirmPasswordPopupComponent,
    ExternalLinkPopupComponent
  ],
  exports: [RouterModule, TranslateModule],
  imports: [
    IonicModule,
    CommonModule,
    SharedModule,
    BrowserAnimationsModule,
    RouterModule.forRoot([]),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpBackend, CORE_I18N_FOLDER]
      }
    }),
    StoreModule.forRoot(
      { core: coreReducer, http: coreHttpReducer, router: routerReducer },
      {
        metaReducers,
        initialState: getCoreModuleInitialState,
        runtimeChecks: {
          strictStateImmutability: false,
          strictActionImmutability: false,
          strictActionWithinNgZone: false // Set true to log info if any action is running outside NgZone
        }
      }
    ),
    EffectsModule.forRoot([
      CoreEffects,
      CoreEffectsMeasure,
      CoreEffectsPlatform,
      CoreRouterEffects,
      CoreHttpEffects
    ]),
    StoreDevtoolsModule.instrument({
      stateSanitizer: inclusionMessage, // Sends no data to DevTools
      maxAge: 100, // Retains last 25 states
      connectInZone: true
    }),
    StoreRouterConnectingModule.forRoot({
      serializer: FullRouterStateSerializer
    }),
    ReactiveFormsModule
  ],
  providers: [
    CoreApiService,
    CoreHttpClient,
    CorePopupService,
    CoreUtilsService,
    BiometricService,
    CoreExternalAppService,
    {
      provide: CORE_BACKEND,
      useValue: CoreBackendIDs.MOBI
    },
    { provide: CORE_PAGE_MODULE, useValue: {}, multi: true },
    { provide: CORE_I18N_FOLDER, useValue: './core/i18n/', multi: true },
    { provide: CORE_I18N_FOLDER, useValue: './common/i18n/', multi: true },
    { provide: CORE_I18N_FOLDER, useValue: './shared/i18n/', multi: true },
    { provide: RouterStateSerializer, useClass: CoreRouterSerializer },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CoreHttpInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CoreHttpResponseInterceptor,
      multi: true
    },
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class CoreModule {
  // prettier-ignore
  constructor() {
    const parentModule = inject(CoreModule, { optional: true, skipSelf: true });

    if (parentModule) {
      throw new Error('CoreModule has already been loaded !!');
    }
  }

  /**
   * Initialise le module core de l'application
   * @param config L'objet de configuration
   */
  static forRoot(config: CoreConfig): ModuleWithProviders<CoreModule> {
    return {
      ngModule: CoreModule,
      providers: [
        CoreInitializer,
        // { provide: CORE_PAGE_MODULE, useValue: myFormModule, multi: true },
        { provide: CORE_CONFIG, useValue: config },
        provideAppInitializer(() => {
          const initializerFn = getForRootInitializer(inject(CoreInitializer));
          return initializerFn();
        })
      ]
    };
  }
}
// export class QuizzModule {
//   constructor(coreUtilsService: CoreUtilsService) {
//     coreUtilsService.addDepthModuleRoutes(myFormModule);
//   }
// }

export function getForAppInitializer(c: CoreInitializer) {
  return c.forAppInitializer.bind(c);
}

export function getForRootInitializer(c: CoreInitializer) {
  return c.forRootInitializer.bind(c);
}

export function localStorageSyncReducer(
  reducer: ActionReducer<any>
): ActionReducer<any> {
  return localStorageSync({
    keys: [
      {
        core: ['instancesData', 'instanceEnv', 'login', 'groupImage']
      }
    ],
    rehydrate: true,
    removeOnUndefined: true
  })(reducer);
}

export function createTranslateLoader(http: HttpBackend, folders: string[]) {
  const dateNow = format(new Date(), 'yyyy-MM-dd-HH-mm');
  const versionType =
    environment && environment?.version
      ? environment.version + '&d=' + dateNow
      : dateNow;
  return new MultiTranslateHttpLoader(
    http,
    folders.map(folder => ({
      prefix: folder,
      suffix: `.json?v=${versionType}`
    }))
  );
}

export function inclusionMessage() {
  return { hidden: 'Inclusion crashes devtools' };
}
